import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, Send, Users, Eye, MousePointerClick, AlertTriangle, History, Loader2, BarChart3, Zap, DollarSign, Trophy, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEGMENTS = [
  { key: 'all',       label: 'All customers',     desc: 'Everyone who opted in (with an email)' },
  { key: 'vip',       label: 'VIPs',              desc: 'Spent >$500 in 90 days or >$1000 ever' },
  { key: 'sleeping',  label: 'Sleeping beauties', desc: 'No visit 60-180 days, has spent >$200' },
  { key: 'new',       label: 'New customers',     desc: 'Signed up in last 30 days' },
  { key: 'regular',   label: 'Regulars',          desc: 'Visited in last 30 days, 2+ purchases' },
  { key: 'birthday',  label: 'Birthday this month', desc: 'Has DOB this month' },
  { key: 'cold',      label: 'Cold customers',    desc: 'No visit 180+ days — last attempt before remove' },
];

const TEMPLATES = [
  {
    name: 'New product drop',
    subject: 'Just landed — fresh in store',
    body: `<p>Hi {{first_name}},</p>
<p>We've just unpacked something we think you'll love.</p>
<h3>This week's new arrival</h3>
<p>[Add product name + 2 lines about it]</p>
<p><a href="https://thewellnessstore.co.nz/collections/new" style="background:#1b5e20;color:#fff;padding:12px 24px;text-decoration:none;border-radius:4px;display:inline-block">Shop new arrivals →</a></p>
<p>See you in store,<br>The Wellness Store team</p>`,
  },
  {
    name: 'Weekend sale',
    subject: 'This weekend only — 20% off',
    body: `<p>Hi {{first_name}},</p>
<p>Quick heads-up — we're running 20% off [category] this Saturday + Sunday only.</p>
<p>In store and online. Use code <strong>WEEKEND20</strong>.</p>
<p>In store this weekend.</p>
<p>See you soon,<br>The team</p>`,
  },
  {
    name: 'We miss you',
    subject: "It's been a while, {{first_name}}",
    body: `<p>Hi {{first_name}},</p>
<p>We noticed it's been a few weeks since you popped in — just wanted to say hi 👋</p>
<p>Pop in this week and we'll give you <strong>15% off your next purchase</strong>. Just show this email at the till.</p>
<p>Hope to see you soon,<br>The Wellness Store team</p>`,
  },
  {
    name: 'Birthday treat',
    subject: 'Happy birthday from The Wellness Store 🎂',
    body: `<p>Happy birthday {{first_name}}! 🎂</p>
<p>Treat yourself — we've got a <strong>$10 voucher</strong> waiting for you in store. Valid all month.</p>
<p>Just mention this email when you visit.</p>
<p>Have a wonderful day,<br>The Wellness Store team</p>`,
  },
];

// Mirror of the backend `_ensure_html_body` — keeps the on-screen preview in
// sync with what recipients actually see. Detects block-level HTML and leaves
// it alone; otherwise wraps plain text in <p>/<br> while preserving inline tags
// (so an inserted <img> renders correctly even when the rest is plain text).
function ensureHtmlBody(body) {
  if (!body) return body;
  const hasBlockHtml = /<\s*(p|div|h[1-6]|ul|ol|table|blockquote)\b/i.test(body);
  if (hasBlockHtml) return body;

  const hasAnyHtml = /<\s*(img|a|strong|em|b|i|span|br)\b/i.test(body);

  const paragraphs = body.trim().split(/\n\s*\n/).filter(p => p.trim());
  return paragraphs.map(p => {
    let txt = p.trim();
    if (!hasAnyHtml) {
      txt = txt
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1">$1</a>');
    }
    return `<p>${txt.replace(/\n/g, '<br>')}</p>`;
  }).join('\n');
}



export default function MarketingEmail() {
  const { getAuthHeader, user } = useAuth();
  const [config, setConfig] = useState(null);
  const [segmentCounts, setSegmentCounts] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [unsubs, setUnsubs] = useState([]);

  const [subject, setSubject] = useState('');
  const [preview, setPreview] = useState('');
  const [body, setBody] = useState('');
  const [segment, setSegment] = useState('all');
  const [testEmail, setTestEmail] = useState('');
  // Resend diagnostic state — gives the user the actual API error instead of
  // silent "all emails failed" from the BackgroundTask path.
  const [diagRunning, setDiagRunning] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [quotaResult, setQuotaResult] = useState(null);
  const [quotaChecking, setQuotaChecking] = useState(false);

  const checkResendQuota = async () => {
    setQuotaChecking(true);
    setQuotaResult(null);
    try {
      const res = await axios.get(`${API_URL}/marketing/quota`, { headers: getAuthHeader(), timeout: 30000 });
      setQuotaResult(res.data);
      if (res.data.ok) {
        toast.success(`Resend OK — ${res.data.monthly_remaining ?? '?'} sends left this month`);
      } else {
        toast.error('Quota check failed — see details');
      }
    } catch (e) {
      setQuotaResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setQuotaChecking(false);
    }
  };

  const runResendDiagnostic = async () => {
    if (!testEmail.trim()) {
      toast.error('Enter an email address to send the test to.');
      return;
    }
    setDiagRunning(true);
    setDiagResult(null);
    try {
      const res = await axios.post(`${API_URL}/marketing/test-send`,
        { to: testEmail.trim(), subject: subject || undefined, body: body || undefined },
        { headers: getAuthHeader(), timeout: 30000 }
      );
      setDiagResult(res.data);
      if (res.data.ok) {
        toast.success('Resend test send succeeded — check your inbox.');
      } else {
        toast.error('Resend rejected the send — see details below.');
      }
    } catch (e) {
      setDiagResult({
        ok: false,
        error: e.response?.data?.detail || e.message || 'Unknown network error',
        hint: 'Could not reach the backend. Check that the server is running and try again.',
      });
    } finally {
      setDiagRunning(false);
    }
  };
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(false);
  const [activeTab, setActiveTab] = useState('compose'); // compose / dashboard / automations / tiers / history / unsubscribes

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const bodyTextareaRef = React.useRef(null);
  const imageFileRef = React.useRef(null);

  const handleInsertImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('That doesn\'t look like an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`Image too large (${Math.round(file.size/1024)} KB). Max is 5 MB.`);
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/marketing/images/upload`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      // Construct the URL ourselves using the PUBLIC backend URL the frontend
      // already knows about — the backend's `request.base_url` returns the
      // internal cluster hostname (something.cluster-N.deploy.emergentcf.cloud)
      // which Gmail/Outlook cannot fetch. REACT_APP_BACKEND_URL is the actual
      // public production domain, which is what we need.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/marketing/images/${res.data.id}`;
      // Build the <img> tag to insert
      const imgTag = `\n<img src="${url}" alt="${file.name.replace(/"/g, '')}" style="max-width:100%;height:auto;display:block;margin:12px 0" />\n`;
      // Insert at cursor position in the textarea
      const ta = bodyTextareaRef.current;
      if (ta) {
        const start = ta.selectionStart ?? body.length;
        const end = ta.selectionEnd ?? body.length;
        const next = body.slice(0, start) + imgTag + body.slice(end);
        setBody(next);
        // Move cursor to just after the inserted image
        setTimeout(() => {
          ta.focus();
          const newPos = start + imgTag.length;
          ta.setSelectionRange(newPos, newPos);
        }, 0);
      } else {
        setBody(body + imgTag);
      }
      // Auto-open the preview so the user immediately SEES the image (not the raw tag)
      setPreviewHtml(true);
      toast.success(`Image inserted — see preview below (${Math.round(file.size/1024)} KB)`);
    } catch (err) {
      toast.error(`Image upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  // Dashboard state
  const [dashboard, setDashboard] = useState(null);

  // Automations state
  const [automations, setAutomations] = useState({});
  const [autoLog, setAutoLog] = useState([]);

  // Tiers state
  const [tierOverview, setTierOverview] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [cfg, segs, hist] = await Promise.all([
        axios.get(`${API_URL}/marketing/config`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/marketing/segments`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/marketing/campaigns`, { headers: getAuthHeader() }),
      ]);
      setConfig(cfg.data);
      setSegmentCounts(segs.data?.counts || {});
      setCampaigns(hist.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [getAuthHeader]);

  const refreshUnsubs = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/marketing/unsubscribes`, { headers: getAuthHeader() });
      setUnsubs(r.data || []);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const refreshDashboard = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/growth/marketing/dashboard?days=30`, { headers: getAuthHeader() });
      setDashboard(r.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const refreshAutomations = useCallback(async () => {
    try {
      const [a, l] = await Promise.all([
        axios.get(`${API_URL}/growth/automations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/growth/automations/log?limit=50`, { headers: getAuthHeader() }),
      ]);
      setAutomations(a.data || {});
      setAutoLog(l.data || []);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const refreshTiers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/growth/loyalty/overview`, { headers: getAuthHeader() });
      setTierOverview(r.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const saveAutomation = async (kind, updates) => {
    try {
      const next = { ...(automations[kind] || {}), ...updates };
      await axios.put(`${API_URL}/growth/automations/${kind}`, next, { headers: getAuthHeader() });
      setAutomations(prev => ({ ...prev, [kind]: next }));
      toast.success(`${kind} saved`);
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const runAutomationsNow = async () => {
    try {
      const r = await axios.post(`${API_URL}/growth/automations/run-now`, {}, { headers: getAuthHeader() });
      toast.success(`Sent ${r.data.winback_sent} win-back, ${r.data.birthday_sent} birthday emails`);
      refreshAutomations();
    } catch (e) {
      toast.error('Run failed');
    }
  };

  useEffect(() => { refresh(); }, [refresh]);

  const applyTemplate = (t) => {
    const biz = user?.business_name || 'our store';
    setSubject(t.subject.replace(/The Wellness Store/g, biz));
    setBody(t.body.replace(/The Wellness Store/g, biz));
    setPreview(t.subject.replace(/The Wellness Store/g, biz));
  };

  const doSend = async (isTest) => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim()) { toast.error('Email body is required'); return; }
    if (isTest && !testEmail.trim()) { toast.error('Enter a test email address'); return; }

    const recipientCount = isTest ? 1 : (segmentCounts[segment] || 0);
    if (!isTest && recipientCount === 0) { toast.error('No recipients in this segment'); return; }

    if (!isTest) {
      const ok = window.confirm(`Send to ${recipientCount} customers in "${segment}"? This cannot be undone.`);
      if (!ok) return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/marketing/send`, {
        subject,
        body_html: body,
        segment,
        preview_text: preview,
        test_email: isTest ? testEmail.trim() : null,
      }, { headers: getAuthHeader(), timeout: 30000 });  // long enough for queueing, way short of 120s timeout

      if (res.data.status === 'queued') {
        // Real (large) campaign — backend kicked off a background task and returned immediately
        toast.success(`📨 Queued ${res.data.total} emails — sending in the background. Track progress in the History tab.`, { duration: 8000 });
        setSubject(''); setBody(''); setPreview('');
        setActiveTab('history');  // switch them to the history view so they can see progress
        refresh();
      } else {
        // Test email — synchronous result
        toast.success(`Sent to ${res.data.sent} — ${res.data.failed} failed`);
        refresh();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const recipientCount = segmentCounts[segment] || 0;

  return (
    <div className="space-y-6 max-w-7xl" data-testid="marketing-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#1b5e20]">Marketing Emails</h1>
        <p className="text-sm text-slate-500 mt-1">Send broadcasts and targeted emails to your customers. NZ anti-spam law compliant — unsubscribe link auto-added.</p>
      </div>

      {config && config.is_test_sender && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3" data-testid="marketing-test-sender-warning">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Test sender mode</p>
            <p className="text-xs">Emails will send FROM <strong>{config.sender_email}</strong>. To send from <strong>{config.reply_to || 'info@thewellnessstore.co.nz'}</strong>, verify your domain in Resend → Domains. I can walk you through it (5 min).</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {[
          { key: 'compose', label: 'Compose', icon: Mail },
          { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { key: 'automations', label: 'Automations', icon: Zap },
          { key: 'tiers', label: 'Loyalty Tiers', icon: Trophy },
          { key: 'history', label: `History (${campaigns.length})`, icon: History },
          { key: 'unsubscribes', label: 'Unsubscribes', icon: Users },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => {
              setActiveTab(t.key);
              if (t.key === 'unsubscribes') refreshUnsubs();
              if (t.key === 'dashboard') refreshDashboard();
              if (t.key === 'automations') refreshAutomations();
              if (t.key === 'tiers') refreshTiers();
            }}
            data-testid={`marketing-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === t.key ? 'border-[#1b5e20] text-[#1b5e20]' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT — composer */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">1. Pick a segment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {SEGMENTS.map(s => {
                  const count = segmentCounts[s.key] || 0;
                  const active = segment === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setSegment(s.key)}
                      data-testid={`segment-${s.key}`}
                      className={`w-full text-left rounded-lg border p-3 transition-all ${
                        active ? 'border-[#1b5e20] bg-emerald-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-800">{s.label}</p>
                          <p className="text-xs text-slate-500">{s.desc}</p>
                        </div>
                        <Badge variant="outline" className={active ? 'bg-[#1b5e20] text-white border-[#1b5e20]' : 'bg-slate-100'}>
                          {count}
                        </Badge>
                      </div>
                    </button>
                  );
                })}
                <p className="text-xs text-slate-500 pt-2">
                  {segmentCounts.unsubscribed > 0 && <>⚠️ {segmentCounts.unsubscribed} customers have unsubscribed and won't receive emails.</>}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">2. Write the email</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500 self-center mr-1">Start from template:</span>
                  {TEMPLATES.map(t => (
                    <button
                      key={t.name}
                      onClick={() => applyTemplate(t)}
                      data-testid={`template-${t.name.toLowerCase().replace(/\s/g, '-')}`}
                      className="text-xs px-3 py-1 rounded-full border border-slate-300 hover:border-[#1b5e20] hover:text-[#1b5e20] transition-colors"
                    >
                      {t.name}
                    </button>
                  ))}
                </div>

                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    data-testid="campaign-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Just landed — fresh in store"
                  />
                </div>

                <div>
                  <Label htmlFor="preview">Preview text <span className="text-slate-400 text-xs">(shown in inbox before opening)</span></Label>
                  <Input
                    id="preview"
                    data-testid="campaign-preview"
                    value={preview}
                    onChange={(e) => setPreview(e.target.value)}
                    placeholder="See what's new this week..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="body">Email body <span className="text-slate-400 text-xs">(paste plain text from ChatGPT/Word — line breaks are kept. HTML also supported. Use {`{{first_name}}`} for personalisation)</span></Label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={imageFileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleInsertImage}
                        className="hidden"
                        data-testid="image-file-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => imageFileRef.current?.click()}
                        disabled={uploadingImage}
                        className="text-xs h-8"
                        data-testid="insert-image-btn"
                      >
                        {uploadingImage ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…</>
                        ) : (
                          <><ImagePlus className="w-3 h-3 mr-1" /> Insert image</>
                        )}
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    id="body"
                    ref={bodyTextareaRef}
                    data-testid="campaign-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Hi {{first_name}},&#10;&#10;Paste straight from your notes or ChatGPT — paragraphs & line breaks are kept.&#10;&#10;Kind regards,&#10;Ritu"
                    rows={12}
                    className="font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setPreviewHtml(p => !p)}
                    className="text-xs text-[#1b5e20] underline mt-1"
                  >
                    {previewHtml ? 'Hide preview' : 'Show preview'}
                  </button>
                  {previewHtml && (
                    <div
                      className="mt-2 border rounded-lg p-4 bg-white prose prose-sm max-w-none"
                      data-testid="body-preview"
                      dangerouslySetInnerHTML={{ __html: ensureHtmlBody(body) }}
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">3. Test, then send</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    data-testid="test-email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="your-email@example.com"
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={() => doSend(true)}
                    disabled={sending}
                    data-testid="send-test-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send test'}
                  </Button>
                </div>

                {/* Diagnostic test — calls /marketing/test-send and surfaces the
                    REAL Resend error so the user can fix it (unverified domain,
                    bad API key, rate limit, etc.) instead of seeing silent
                    "all failed" on the campaign list. */}
                <Button
                  variant="outline"
                  onClick={runResendDiagnostic}
                  disabled={diagRunning || !testEmail.trim()}
                  className="w-full border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="run-resend-diag-btn"
                >
                  {diagRunning
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Running diagnostic…</>
                    : <>🩺 Diagnose Resend (sends 1 test email, shows real error)</>}
                </Button>
                {diagResult && (
                  <div
                    className={`text-xs rounded-lg border-2 p-3 ${diagResult.ok ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-red-300 bg-red-50 text-red-900'}`}
                    data-testid="resend-diag-result"
                  >
                    {diagResult.ok ? (
                      <>
                        <p className="font-bold mb-1">✓ Resend is configured correctly</p>
                        <p className="text-[11px] font-mono break-all">
                          Sent from <span className="font-bold">{diagResult.from}</span> to <span className="font-bold">{diagResult.to}</span>
                        </p>
                        {diagResult.message_id && (
                          <p className="text-[11px] font-mono text-emerald-700 mt-0.5">Message ID: {diagResult.message_id}</p>
                        )}
                        <p className="mt-1.5">Check the inbox (and spam folder) for "{subject || 'SupplyTrack — Resend test email'}".</p>
                      </>
                    ) : (
                      <>
                        <p className="font-bold mb-1">✗ Resend send failed</p>
                        <p className="text-[11px] font-mono break-all bg-red-100 px-2 py-1 rounded">
                          {diagResult.error || 'No error detail returned'}
                        </p>
                        {diagResult.hint && (
                          <p className="mt-2 leading-snug">
                            <span className="font-bold">💡 Fix:</span> {diagResult.hint}
                          </p>
                        )}
                        <p className="text-[11px] mt-2 text-red-700">
                          FROM was: <span className="font-mono">{diagResult.from}</span>
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Live Resend quota check — sends 1 probe email to Resend's
                    sandbox address so we can read the rate-limit + remaining
                    monthly quota headers. Critical to run BEFORE a 900-person
                    blast: if you only have 200 sends left this month, the
                    other 700 will all fail with quota-exceeded errors. */}
                <Button
                  variant="outline"
                  onClick={checkResendQuota}
                  disabled={quotaChecking}
                  className="w-full border-sky-300 text-sky-800 hover:bg-sky-50"
                  data-testid="check-resend-quota-btn"
                >
                  {quotaChecking
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Checking quota…</>
                    : <>📊 Check Resend quota (rate limit & monthly remaining)</>}
                </Button>
                {quotaResult && (
                  <div
                    className={`text-xs rounded-lg border-2 p-3 ${quotaResult.ok ? 'border-sky-300 bg-sky-50 text-sky-900' : 'border-red-300 bg-red-50 text-red-900'}`}
                    data-testid="resend-quota-result"
                  >
                    {quotaResult.ok ? (
                      <>
                        <p className="font-bold mb-1.5">📊 Resend Account Limits</p>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-sky-700">Rate limit</div>
                            <div className="text-base font-bold">{quotaResult.rate_limit_per_sec ?? '?'}<span className="text-xs font-normal">/sec</span></div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-sky-700">Daily left</div>
                            <div className="text-base font-bold">{quotaResult.daily_remaining ?? '∞'}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-sky-700">Monthly left</div>
                            <div className={`text-base font-bold ${(quotaResult.monthly_remaining ?? 9999) < recipientCount ? 'text-red-700' : 'text-emerald-700'}`}>
                              {quotaResult.monthly_remaining ?? '∞'}
                            </div>
                          </div>
                        </div>
                        {quotaResult.monthly_remaining != null && recipientCount > quotaResult.monthly_remaining && (
                          <p className="mt-2 leading-snug font-bold text-red-700 bg-red-100 p-2 rounded">
                            ⚠️ You have {quotaResult.monthly_remaining} sends left this month but you&apos;re about to send to {recipientCount} customers. {recipientCount - quotaResult.monthly_remaining} will fail. Upgrade your Resend plan first.
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="font-bold mb-1">✗ Quota check failed</p>
                        <p className="text-[11px] font-mono break-all">{quotaResult.error}</p>
                      </>
                    )}
                  </div>
                )}

                <div className="border-t pt-3">
                  <Button
                    onClick={() => doSend(false)}
                    disabled={sending || recipientCount === 0}
                    className="w-full bg-[#1b5e20] hover:bg-emerald-800 text-white h-12 text-base"
                    data-testid="send-campaign-btn"
                  >
                    {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send to {recipientCount} customers</>}
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-2">Once sent, this cannot be undone. Always test first!</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT — quick stats */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-slate-500">Sender</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p><strong>{config?.sender_name}</strong></p>
                <p className="text-slate-600">{config?.sender_email}</p>
                {config?.reply_to && <p className="text-xs text-slate-500">Replies → {config.reply_to}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-slate-500">Tips</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-slate-600 space-y-2">
                <p>• Keep subject lines under 50 chars for mobile.</p>
                <p>• Send Tue–Thu, 10am–1pm for best open rates.</p>
                <p>• Always send a <strong>test</strong> to yourself first.</p>
                <p>• Use <code>{`{{first_name}}`}</code> in subject + body for personalisation.</p>
                <p>• Unsubscribe link is added automatically.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Past campaigns</CardTitle>
            <Button variant="outline" size="sm" onClick={refresh} data-testid="refresh-history-btn">
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No campaigns sent yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="campaigns-table">
                  <thead className="text-xs uppercase text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-2">When</th>
                      <th className="text-left py-2">Subject</th>
                      <th className="text-left py-2">Segment</th>
                      <th className="text-right py-2">Progress</th>
                      <th className="text-right py-2"><Eye className="w-3 h-3 inline" /> Opens</th>
                      <th className="text-right py-2"><MousePointerClick className="w-3 h-3 inline" /> Clicks</th>
                      <th className="text-right py-2">Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(c => {
                      const openPct = c.sent_count > 0 ? Math.round(100 * (c.open_count || 0) / c.sent_count) : 0;
                      const clickPct = c.sent_count > 0 ? Math.round(100 * (c.click_count || 0) / c.sent_count) : 0;
                      const inProgress = c.status === 'queued' || c.status === 'sending';
                      const total = c.recipient_count || 0;
                      const progressPct = total > 0 ? Math.round(100 * ((c.sent_count || 0) + (c.failed_count || 0)) / total) : 0;
                      return (
                        <tr key={c.id} className="border-b hover:bg-slate-50">
                          <td className="py-2 text-slate-600">{formatDate(c.created_at)}</td>
                          <td className="py-2 font-medium">
                            {c.subject}
                            {c.is_test && <Badge variant="outline" className="ml-1 text-[10px]">test</Badge>}
                            {inProgress && <Badge className="ml-1 text-[10px] bg-amber-500 text-white animate-pulse">{c.status}</Badge>}
                          </td>
                          <td className="py-2"><Badge variant="outline" className="capitalize">{c.segment}</Badge></td>
                          <td className="py-2 text-right">
                            {inProgress ? (
                              <span className="text-amber-700 font-semibold">{c.sent_count}/{total} ({progressPct}%)</span>
                            ) : (
                              <span>{c.sent_count}</span>
                            )}
                          </td>
                          <td className="py-2 text-right">{c.open_count || 0} <span className="text-xs text-slate-400">({openPct}%)</span></td>
                          <td className="py-2 text-right">{c.click_count || 0} <span className="text-xs text-slate-400">({clickPct}%)</span></td>
                          <td className="py-2 text-right text-red-500" title={(c.failure_breakdown || c.failed_reasons || []).map(r => typeof r === 'string' ? r : `${r.reason} (×${r.count})`).join('\n') || ''}>
                            {c.failed_count || 0}
                            {c.status === 'aborted' && c.aborted_reason && (
                              <span className="block text-[10px] font-bold text-red-700 max-w-[260px] mt-0.5" data-testid={`aborted-reason-${c.id}`}>
                                ⛔ {c.aborted_reason}
                              </span>
                            )}
                            {(c.failure_breakdown || []).length > 0 ? (
                              <span className="block text-[10px] font-normal text-red-600 max-w-[260px] mt-0.5" data-testid={`failed-reason-${c.id}`}>
                                {c.failure_breakdown.slice(0, 2).map(r => `${r.reason} (×${r.count})`).join(' · ')}
                                {c.failure_breakdown.length > 2 && ` +${c.failure_breakdown.length - 2} more`}
                              </span>
                            ) : (c.failed_reasons || []).length > 0 && (
                              <span className="block text-[10px] font-normal text-red-600 max-w-[200px] truncate" data-testid={`failed-reason-${c.id}`}>
                                {c.failed_reasons[0]}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'unsubscribes' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Unsubscribed</CardTitle>
          </CardHeader>
          <CardContent>
            {unsubs.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No one has unsubscribed yet.</p>
            ) : (
              <div className="space-y-1" data-testid="unsubscribes-list">
                {unsubs.map(u => (
                  <div key={u.email} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                    <span className="text-slate-700">{u.email}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{formatDate(u.unsubscribed_at)}</span>
                      <button
                        onClick={async () => {
                          await axios.post(`${API_URL}/marketing/resubscribe`, { email: u.email }, { headers: getAuthHeader() });
                          toast.success(`${u.email} re-subscribed`);
                          refreshUnsubs(); refresh();
                        }}
                        className="text-xs text-[#1b5e20] underline hover:no-underline"
                      >
                        Re-add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'dashboard' && (
        <div className="space-y-4">
          {!dashboard ? (
            <p className="text-sm text-slate-500 py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading dashboard...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Campaigns sent', value: dashboard.summary.campaigns, icon: Mail },
                  { label: 'Emails delivered', value: dashboard.summary.emails_sent, icon: Send },
                  { label: 'Open rate', value: `${dashboard.summary.open_rate}%`, icon: Eye },
                  { label: 'Click rate', value: `${dashboard.summary.click_rate}%`, icon: MousePointerClick },
                ].map(s => (
                  <Card key={s.label}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</p>
                          <p className="text-2xl font-bold text-slate-800 mt-1">{s.value}</p>
                        </div>
                        <s.icon className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-700 uppercase tracking-wide flex items-center gap-1"><DollarSign className="w-3 h-3" /> Revenue attributed to email</p>
                      <p className="text-3xl font-bold text-emerald-900 mt-1" data-testid="attributed-revenue">${dashboard.summary.attributed_revenue.toFixed(2)}</p>
                      <p className="text-xs text-emerald-600 mt-1">{dashboard.summary.attributed_purchases} purchases within 7 days of receiving a campaign · last 30 days</p>
                    </div>
                    <DollarSign className="w-12 h-12 text-emerald-300" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Top campaigns by revenue</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-slate-500 border-b">
                        <tr>
                          <th className="text-left py-2">Subject</th>
                          <th className="text-left py-2">Sent</th>
                          <th className="text-left py-2">Opens</th>
                          <th className="text-left py-2">Clicks</th>
                          <th className="text-right py-2">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(dashboard.campaigns || [])].sort((a,b) => (b.attributed_revenue || 0) - (a.attributed_revenue || 0)).slice(0, 10).map(c => (
                          <tr key={c.id} className="border-b">
                            <td className="py-2 font-medium">{c.subject}</td>
                            <td className="py-2">{c.sent_count}</td>
                            <td className="py-2">{c.open_count || 0} <span className="text-xs text-slate-400">({c.open_rate}%)</span></td>
                            <td className="py-2">{c.click_count || 0} <span className="text-xs text-slate-400">({c.click_rate}%)</span></td>
                            <td className="py-2 text-right font-bold text-emerald-700">${(c.attributed_revenue || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {activeTab === 'automations' && (
        <div className="space-y-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-sm text-amber-900">
              <p className="font-semibold">How automations work</p>
              <p className="text-xs mt-1">Toggle on, and these emails will fire automatically based on customer behaviour. Daily check at ~midnight (Welcome fires immediately on customer creation). Each customer is emailed at most once per automation (Birthday: once per year).</p>
              <Button size="sm" variant="outline" className="mt-3 border-amber-400 text-amber-900" onClick={runAutomationsNow} data-testid="run-automations-now">
                Run now (test)
              </Button>
            </CardContent>
          </Card>

          {['welcome', 'winback', 'birthday'].map(kind => {
            const a = automations[kind] || {};
            const titles = {
              welcome: { t: '👋 Welcome email', d: 'Sent when a new customer is added with an email address.' },
              winback: { t: '💤 Win-back', d: `Sent to customers who haven't visited in ${a.days_inactive || 60}+ days.` },
              birthday: { t: '🎂 Birthday treat', d: 'Sent in the week of the customer\'s birthday (DOB on profile required).' },
            };
            return (
              <Card key={kind} data-testid={`automation-${kind}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{titles[kind].t}</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">{titles[kind].d}</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!a.enabled}
                      onChange={(e) => saveAutomation(kind, { enabled: e.target.checked, subject: a.subject, body_html: a.body_html, days_inactive: a.days_inactive })}
                      data-testid={`automation-${kind}-toggle`}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm font-semibold ${a.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>{a.enabled ? 'On' : 'Off'}</span>
                  </label>
                </CardHeader>
                <CardContent className="space-y-3">
                  {kind === 'winback' && (
                    <div>
                      <Label>Days inactive before sending</Label>
                      <Input type="number" value={a.days_inactive || 60} onChange={(e) => setAutomations(p => ({ ...p, [kind]: { ...a, days_inactive: parseInt(e.target.value) || 60 } }))} className="w-32" />
                    </div>
                  )}
                  <div>
                    <Label>Subject</Label>
                    <Input value={a.subject || ''} onChange={(e) => setAutomations(p => ({ ...p, [kind]: { ...a, subject: e.target.value } }))} />
                  </div>
                  <div>
                    <Label>Body (HTML)</Label>
                    <Textarea rows={6} className="font-mono text-xs" value={a.body_html || ''} onChange={(e) => setAutomations(p => ({ ...p, [kind]: { ...a, body_html: e.target.value } }))} />
                  </div>
                  <Button size="sm" onClick={() => saveAutomation(kind, { enabled: a.enabled, subject: a.subject, body_html: a.body_html, days_inactive: a.days_inactive })}>Save changes</Button>
                </CardContent>
              </Card>
            );
          })}

          <Card>
            <CardHeader><CardTitle className="text-lg">Recent automation sends ({autoLog.length})</CardTitle></CardHeader>
            <CardContent>
              {autoLog.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No automation emails sent yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b">
                    <tr><th className="text-left py-2">When</th><th className="text-left py-2">Type</th><th className="text-left py-2">To</th><th className="text-left py-2">Subject</th></tr>
                  </thead>
                  <tbody>
                    {autoLog.map((l, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2 text-slate-600">{formatDate(l.sent_at)}</td>
                        <td className="py-2"><Badge variant="outline" className="capitalize">{l.kind}</Badge></td>
                        <td className="py-2">{l.email}</td>
                        <td className="py-2 text-slate-600">{l.subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'tiers' && (
        <div className="space-y-4">
          {!tierOverview ? (
            <p className="text-sm text-slate-500 py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {tierOverview.tiers.map(t => {
                  const colors = {
                    bronze:   'bg-amber-100 text-amber-900 border-amber-300',
                    silver:   'bg-slate-200 text-slate-800 border-slate-300',
                    gold:     'bg-yellow-100 text-yellow-900 border-yellow-300',
                    platinum: 'bg-slate-100 text-slate-900 border-slate-400',
                  };
                  return (
                    <Card key={t.key} className={`border-2 ${colors[t.key] || 'bg-white'}`}>
                      <CardContent className="p-4">
                        <p className="text-xs uppercase tracking-wide opacity-70">{t.label}</p>
                        <p className="text-3xl font-bold mt-1">{tierOverview.counts[t.key] || 0}</p>
                        <p className="text-[10px] mt-1 opacity-70">$ ≥ {t.min_spent} lifetime</p>
                        <ul className="text-[11px] mt-2 space-y-0.5">
                          {(t.perks || []).slice(0,3).map((p, i) => <li key={i}>• {p}</li>)}
                        </ul>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card>
                <CardHeader><CardTitle className="text-lg">Top 25 customers by lifetime spend</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-slate-500 border-b">
                        <tr>
                          <th className="text-left py-2">#</th>
                          <th className="text-left py-2">Customer</th>
                          <th className="text-left py-2">Tier</th>
                          <th className="text-right py-2">Lifetime $</th>
                          <th className="text-left py-2 pl-3">Last visit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tierOverview.top_customers.map((c, i) => (
                          <tr key={c.id} className="border-b">
                            <td className="py-2">{i+1}</td>
                            <td className="py-2 font-medium">{c.name}<br/><span className="text-[10px] text-slate-400">{c.phone}</span></td>
                            <td className="py-2"><Badge variant="outline" className="capitalize">{c.tier_label}</Badge></td>
                            <td className="py-2 text-right font-bold text-emerald-700">${c.total_spent.toFixed(2)}</td>
                            <td className="py-2 pl-3 text-slate-500 text-xs">{formatDate(c.last_visit)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
